import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, message, Space, Breadcrumb, Row, Col, Input, Radio, Modal, Switch, Tag, DatePicker } from 'antd';
import { FolderOpenOutlined, FileOutlined, DownloadOutlined, EyeOutlined, HomeOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { MdOutlineDriveFileRenameOutline } from 'react-icons/md';
import { AiOutlineDelete } from 'react-icons/ai';
import dayjs from 'dayjs';
import Button from '../../components/common/Button';
import { ProtectedRoute, PageGuard } from '../../components/common/security/ProtectedRoute';
import { useAuth } from '../../components/common/security/AuthContext';
import { useLocation } from 'react-router-dom';
import {
  dccDelete,
  dccDownloadUrl,
  dccIndexRefresh,
  dccList,
  dccListStats,
  dccMove,
  dccSearch,
  type DccListStatsItem,
  type DccSearchType,
  type DccStorageRoot,
} from '../../services/dccApi';
import FilePreviewModal, { canPreviewFile } from '../../components/common/FilePreviewModal';
import StandardTable from '../../components/common/StandardTable';
import { api, fetchSqlData } from '../../services/api';
import { queueNewTranslationLogRows } from '../../utils/dccTranslationQueue';
import type { DccTranslationLogRow } from '../../types';

const { Title, Text } = Typography;

// Wrappers for react-icons to ensure valid JSX element typing (same pattern as route files)
const RDriveFileRename: React.FC<any> = (props) =>
  React.createElement(MdOutlineDriveFileRenameOutline as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RAiDelete: React.FC<any> = (props) =>
  React.createElement(AiOutlineDelete as unknown as React.ComponentType<any>, props) as React.ReactElement;

const SUBFOLDER_SEARCH_LIMIT = 1000;
const SUBFOLDER_SCAN_LIMIT = 500;

const { RangePicker } = DatePicker;

interface DccItemRow {
  key: string;
  name: string;
  path: string;
  isFolder: boolean;
  sizeBytes?: number;
  modifiedAt?: number;
  createdAt?: number;
}

type TranslationQueueDraft = {
  key: string;
  source_file_path: string;
  folder_path: string;
  transmittal_no: string;
  document_no: string;
  revision: string;
};

interface DccDocumentsProps {
  title?: string;
  description?: string;
  storageRoot?: DccStorageRoot;
  allowIndexedSearch?: boolean;
  rootLabel?: string;
}

const DccDocuments: React.FC<DccDocumentsProps> = ({
  title = 'DCC Documents',
  description = 'Browse DCC folders under /root/uploads and view files.',
  storageRoot = 'uploads',
  allowIndexedSearch = true,
  rootLabel = 'Root',
}) => {
  const { token } = useAuth();
  const location = useLocation();
  const [effectiveStorageRoot, setEffectiveStorageRoot] = useState<DccStorageRoot>(storageRoot);
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState<DccItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'folder' | 'file' | 'pdf'>('all');
  const [includeSubfolders, setIncludeSubfolders] = useState(false);
  const [pathFilter, setPathFilter] = useState('');
  const [modifiedRange, setModifiedRange] = useState<[any, any] | null>(null);
  const [searchItems, setSearchItems] = useState<DccItemRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [reindexLoading, setReindexLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DccItemRow[]>([]);
  const [translationStatusBySource, setTranslationStatusBySource] = useState<Record<string, string>>({});
  const [folderHasChildren, setFolderHasChildren] = useState<Record<string, boolean>>({});

  const [queueDraftOpen, setQueueDraftOpen] = useState(false);
  const [queueDraftRows, setQueueDraftRows] = useState<TranslationQueueDraft[]>([]);
  const [queueSaving, setQueueSaving] = useState(false);

  // Remember filter state per folder so that when user goes back up one level,
  // we can restore the parent's previous filters.
  interface FilterState {
    nameFilter: string;
    typeFilter: 'all' | 'folder' | 'file' | 'pdf';
    includeSubfolders: boolean;
    pathFilter: string;
    modifiedRange: [any, any] | null;
  }
  const [filterMemory, setFilterMemory] = useState<Record<string, FilterState>>({});

  const getUniqueBatchRows = (rows: DccItemRow[]): DccItemRow[] => {
    const normalizedRows = [...rows].sort(
      (a, b) => a.path.split('/').filter(Boolean).length - b.path.split('/').filter(Boolean).length
    );

    return normalizedRows.filter((row, index) => {
      const currentPathNormalized = String(row.path || '').replace(/^\/+|\/+$/g, '');
      if (!currentPathNormalized) {
        return true;
      }

      return !normalizedRows.slice(0, index).some((candidate) => {
        if (!candidate.isFolder) {
          return false;
        }

        const folderPathNormalized = String(candidate.path || '').replace(/^\/+|\/+$/g, '');
        if (!folderPathNormalized || folderPathNormalized === currentPathNormalized) {
          return false;
        }

        return currentPathNormalized.startsWith(`${folderPathNormalized}/`);
      });
    });
  };

  const saveCurrentFilterState = () => {
    const key = String(currentPath || '');
    setFilterMemory((prev) => ({
      ...prev,
      [key]: {
        nameFilter,
        typeFilter,
        includeSubfolders,
        pathFilter,
        modifiedRange,
      },
    }));
  };

  const selectedFileRows = useMemo(
    () => selectedRows.filter((row) => !row.isFolder),
    [selectedRows]
  );
  const selectedFolderRows = useMemo(
    () => selectedRows.filter((row) => row.isFolder),
    [selectedRows]
  );
  const selectedBatchTargets = useMemo(
    () => getUniqueBatchRows(selectedRows),
    [selectedRows]
  );

  const mapDccStatsItems = (data: DccListStatsItem[]): DccItemRow[] => {
    return data.map((item: DccListStatsItem) => {
      const name = String(item.name || '').replace(/\\/g, '/');
      const path = String(item.path || name || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');

      return {
        key: path || name,
        name,
        path,
        isFolder: Boolean(item.is_dir),
        sizeBytes: typeof item.size === 'number' ? item.size : undefined,
        modifiedAt: typeof item.modified_at === 'number' ? item.modified_at : undefined,
        createdAt: typeof item.created_at === 'number' ? item.created_at : undefined,
      };
    });
  };

  const matchesTypeFilter = (item: DccItemRow, nextTypeFilter: 'all' | 'folder' | 'file' | 'pdf') => {
    const isPdf = item.name.toLowerCase().endsWith('.pdf');
    switch (nextTypeFilter) {
      case 'folder':
        return item.isFolder;
      case 'file':
        return !item.isFolder;
      case 'pdf':
        return !item.isFolder && isPdf;
      default:
        return true;
    }
  };

  const currentRoot = useMemo(() => {
    if (!allowIndexedSearch) return '';
    const p = String(currentPath || '').trim();
    if (!p) return '';
    return p.split('/').filter(Boolean)[0] || '';
  }, [allowIndexedSearch, currentPath]);

  const handleReindexRoot = async () => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    if (!currentRoot) {
      message.info('Select a folder first to re-index its root.');
      return;
    }

    Modal.confirm({
      title: 'Rebuild search index for this root?',
      content: `This will re-index all subfolders under: ${currentRoot}`,
      okText: 'Re-index',
      cancelText: 'Cancel',
      onOk: async () => {
        setReindexLoading(true);
        try {
          const res = await dccIndexRefresh({ root: currentRoot, storageRoot: effectiveStorageRoot }, token);
          if (res?.success) {
            const count = (res as any).indexed_rows ?? (res as any).last_refresh_count;
            message.success(`Index refreshed for ${currentRoot}${typeof count === 'number' ? ` (${count} rows)` : ''}`);
          } else {
            message.error(res?.error || 'Index refresh failed');
          }
        } catch (e) {
          const msg = String((e as any)?.message || '');
          message.error(msg || 'Index refresh failed');
        } finally {
          setReindexLoading(false);
        }
      },
    });
  };

  const fetchList = async (path = '') => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    setLoading(true);
    try {
      const data = await dccListStats(path, token, effectiveStorageRoot);
      const mapped = mapDccStatsItems(data);
      setItems(mapped);
      setFolderHasChildren({});
      setCurrentPath(path);
      setNameFilter('');
      setTypeFilter('all');
      setIncludeSubfolders(false);
      setSearchItems([]);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      // Clear selected file when folder changes
      setPreviewPath(null);
    } catch (e) {
      const msg = String((e as any)?.message || '');
      message.error(msg || 'Failed to load DCC folder contents.');
    } finally {
      setLoading(false);
    }
  };

  // NOT: Daha once burada her alt klasor icin ayri bir dccList istegi atan
  // "has-children" on-yukleme effect'i vardi. Kalabalik koklerde (orn. MHIDOCMAILS)
  // yuzlerce paralel istek tarayici kuyrugunu doldurup klasore girisleri
  // dakikalarca geciktiriyordu. Bilgi yalnizca silme butonunu onden devre disi
  // gostermek icin kullaniliyordu ve handleDelete zaten tiklama aninda ayni
  // kontrolu on-demand yapiyor; bu yuzden on-yukleme kaldirildi.

  useEffect(() => {
    if (!token) return;

    // Allow opening DCC documents at a specific folder via query param.
    // Example: /dcc/documents?path=some/folder
    const params = new URLSearchParams(location.search || '');
    const raw = (params.get('path') || '').trim();
    const initialPath = raw.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');

    const rootParam = (params.get('root') || params.get('storage_root') || '').trim().toLowerCase();
    const nextRoot: DccStorageRoot = rootParam === 'mhidocmails' ? 'mhidocmails' : 'uploads';
    setEffectiveStorageRoot(nextRoot);
    fetchList(initialPath);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.search, storageRoot]);

  const handleEnterFolder = (record: DccItemRow) => {
    // Save current folder's filters so we can restore them when navigating back up.
    saveCurrentFilterState();
    fetchList(record.path);
  };

  const handleGoUp = async () => {
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    const parentPath = parts.join('/');
    const restore = filterMemory[parentPath];
    await fetchList(parentPath);
    if (restore) {
      setNameFilter(restore.nameFilter);
      setTypeFilter(restore.typeFilter);
      setIncludeSubfolders(restore.includeSubfolders);
      setPathFilter(restore.pathFilter);
      setModifiedRange(restore.modifiedRange);
    }
  };

  const buildFullPath = (record: DccItemRow) => record.path;

  const inferTransmittalNo = (path: string) => {
    const p = String(path || '');
    const m = p.match(/(TKF-[A-Za-z]{2,3}-[TEL]-[0-9]{3,5})/i);
    return m ? String(m[1]).toUpperCase() : '';
  };

  const inferDocNoFromFilename = (filePath: string) => {
    const base = String(filePath || '').replace(/\\/g, '/').split('/').pop() || '';
    if (!base) return '';
    // Strip last extension, but keep dotfiles like `.gitignore` intact.
    const lastDot = base.lastIndexOf('.');
    if (lastDot > 0) {
      return base.slice(0, lastDot);
    }
    return base;
  };

  const normalizePath = (p: string) =>
    String(p || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '');

  const normalizeSourceKey = (p: string) => normalizePath(p).toLowerCase();

  const normalizeItemSourceKey = (item: DccItemRow): string => normalizeSourceKey(buildFullPath(item));

  const getTranslationStatusForItem = (item: DccItemRow): string | undefined => {
    if (!item || item.isFolder) return undefined;
    const srcKey = normalizeItemSourceKey(item);
    return translationStatusBySource[srcKey];
  };

  const isItemAlreadyQueuedForTranslation = (item: DccItemRow): boolean => {
    const st = String(getTranslationStatusForItem(item) || '').toLowerCase();
    return Boolean(st);
  };

  const handleQueueForTranslation = () => {
    if (!selectedFileRows.length) {
      message.info('Select at least one file');
      return;
    }

    const selectedFolderPrefixes = selectedFolderRows
      .map((r) => String(r.path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, ''))
      .filter(Boolean);

    const fileRowsNotUnderSelectedFolders = selectedFolderPrefixes.length
      ? selectedFileRows.filter((r) => {
          const p = String(r.path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
          return !selectedFolderPrefixes.some((folderPath) => p.startsWith(`${folderPath}/`));
        })
      : selectedFileRows;

    const candidates = fileRowsNotUnderSelectedFolders;

    const skippedAlreadyQueued = candidates.filter((r) => isItemAlreadyQueuedForTranslation(r));
    const filesToQueue = candidates.filter((r) => !isItemAlreadyQueuedForTranslation(r));
    if (!filesToQueue.length) {
      message.info(
        skippedAlreadyQueued.length
          ? `All selected file(s) are already queued/sent for translation (${skippedAlreadyQueued.length} skipped).`
          : 'Select at least one file'
      );
      return;
    }

    const folderPath = String(currentPath || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
    const transmittalNo = inferTransmittalNo(folderPath);

    const buildExternalTransmittalNo = () => {
      const parts = String(folderPath || '').split('/').filter(Boolean);
      const parent = String(parts[parts.length - 1] || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 10);
      const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
      return parent ? `TKF-EXT-${parent}-${suffix}` : `TKF-EXT-${suffix}`;
    };

    const resolvedTransmittalNo = String(transmittalNo || '').trim() || buildExternalTransmittalNo();

    const drafts: TranslationQueueDraft[] = filesToQueue.map((r) => {
      const src = String(buildFullPath(r) || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
      return {
        key: src,
        source_file_path: src,
        folder_path: folderPath,
        transmittal_no: resolvedTransmittalNo,
        document_no: inferDocNoFromFilename(src),
        revision: '',
      };
    });

    setQueueDraftRows(drafts);
    setQueueDraftOpen(true);
  };

  const saveQueueDraft = async () => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    if (!queueDraftRows.length) {
      setQueueDraftOpen(false);
      return;
    }
    const bad = queueDraftRows.find((r) => !r.document_no || !r.source_file_path);
    if (bad) {
      message.error('Document no and source file path are required for all rows');
      return;
    }
    try {
      setQueueSaving(true);
      const requestedBy = localStorage.getItem('user') || null;
      const queued = await queueNewTranslationLogRows(
        queueDraftRows.map((r) => ({
          transmittal_no: r.transmittal_no,
          document_no: r.document_no,
          revision: r.revision || '',
          folder_path: r.folder_path,
          source_file_path: r.source_file_path,
          status: 'queued',
          requested_by: requestedBy,
        })),
      );
      message.success(`Queued ${queued} document(s) for translation`);
      setQueueDraftOpen(false);
      setQueueDraftRows([]);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to queue documents');
    } finally {
      setQueueSaving(false);
    }
  };

  const handleDownload = (record: DccItemRow) => {
    const fullPath = buildFullPath(record);
    const url = dccDownloadUrl(fullPath, token, false, effectiveStorageRoot);
    window.open(url, '_blank');
  };

  const handleDelete = async (record: DccItemRow) => {
    const fullPath = buildFullPath(record);

    if (record.isFolder) {
      const cached = folderHasChildren[record.path];
      if (cached === true) {
        message.warning('Cannot delete a folder that contains items. Delete its contents first.');
        return;
      }
      if (cached === undefined) {
        try {
          const data = await dccList(record.path, token, effectiveStorageRoot);
          const has = Array.isArray(data) && data.length > 0;
          setFolderHasChildren((prev) => ({ ...prev, [record.path]: has }));
          if (has) {
            message.warning('Cannot delete a folder that contains items. Delete its contents first.');
            return;
          }
        } catch {
          // If list fails, fall back to existing delete behavior.
        }
      }
    }

    Modal.confirm({
      title: 'Delete item',
      content: (
        <div>
          <div>Are you sure you want to delete:</div>
          <div style={{ marginTop: 8, fontWeight: 600 }}>{fullPath}</div>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        if (!token) {
          message.error('Not authenticated. Please login again.');
          return;
        }
        await dccDelete(fullPath, token, effectiveStorageRoot);
        message.success('Deleted');
        await fetchList(currentPath);
      },
    });
  };

  const handleBatchDownload = () => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    if (!selectedFileRows.length) {
      message.info('Select at least one file to download');
      return;
    }
    selectedFileRows.forEach((r) => {
      const fullPath = buildFullPath(r);
      const url = dccDownloadUrl(fullPath, token, false, effectiveStorageRoot);
      window.open(url, '_blank');
    });
  };

  const handleBatchDelete = () => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    if (!selectedBatchTargets.length) {
      message.info('No items selected');
      return;
    }
    Modal.confirm({
      title: 'Delete selected items',
      content: `Delete ${selectedBatchTargets.length} item(s)?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const results = await Promise.allSettled(
          selectedBatchTargets.map((r) => dccDelete(buildFullPath(r), token, effectiveStorageRoot))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        const ok = results.length - failed;
        if (failed) message.warning(`Deleted ${ok}/${results.length}. ${failed} failed.`);
        else message.success(`Deleted ${ok} item(s)`);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        await fetchList(currentPath);
      },
    });
  };

  const handleRename = async (record: DccItemRow) => {
    const src = buildFullPath(record);
    const parts = src.split('/').filter(Boolean);
    const currentName = parts.pop() || record.name;
    const baseDir = parts.join('/');
    let nextName = currentName;
    const modal = Modal.confirm({
      title: 'Rename item',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>New name:</div>
          <Input defaultValue={currentName} onChange={(e) => { nextName = e.target.value; }} />
        </div>
      ),
      okText: 'Rename',
      cancelText: 'Cancel',
      onOk: async () => {
        const trimmed = String(nextName || '').trim();
        if (!trimmed) {
          message.error('Name cannot be empty');
          throw new Error('validation');
        }
        if (trimmed.includes('/') || trimmed.includes('\\')) {
          message.error('Name cannot include path separators');
          throw new Error('validation');
        }
        const dst = baseDir ? `${baseDir}/${trimmed}` : trimmed;
        if (!token) {
          message.error('Not authenticated. Please login again.');
          return;
        }
        await dccMove(src, dst, token, effectiveStorageRoot);
        message.success('Renamed');
        await fetchList(currentPath);
      },
    });
    return modal;
  };

  const handleOpenParentFolder = async (record: DccItemRow) => {
    const fullPath = buildFullPath(record);
    const parts = fullPath.split('/').filter(Boolean);
    if (parts.length <= 1) {
      // No parent folder or at root: go to root
      const restore = filterMemory[''];
      await fetchList('');
      if (restore) {
        setNameFilter(restore.nameFilter);
        setTypeFilter(restore.typeFilter);
        setIncludeSubfolders(restore.includeSubfolders);
        setPathFilter(restore.pathFilter);
        setModifiedRange(restore.modifiedRange);
      }
      return;
    }
    parts.pop();
    const parentPath = parts.join('/');
    const restore = filterMemory[parentPath];
    await fetchList(parentPath);
    if (restore) {
      setNameFilter(restore.nameFilter);
      setTypeFilter(restore.typeFilter);
      setIncludeSubfolders(restore.includeSubfolders);
      setPathFilter(restore.pathFilter);
      setModifiedRange(restore.modifiedRange);
    }
  };

  const handlePreview = (record: DccItemRow) => {
    const fullPath = buildFullPath(record);
    setPreviewPath(fullPath);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewPath(null);
  };

  const renderNameCell = (record?: DccItemRow) => {
    if (!record) return null;
    const translationStatus = getTranslationStatusForItem(record);
    return record.isFolder ? (
      <span
        style={{ cursor: 'pointer', color: '#1677ff' }}
        onClick={() => handleEnterFolder(record)}
      >
        <FolderOpenOutlined /> {record.name}
      </span>
    ) : (
      <span
        style={{ cursor: 'pointer', color: translationStatus ? '#389e0d' : undefined }}
        onClick={() => {
          if (canPreviewFile(record.name)) handlePreview(record);
          else handleDownload(record);
        }}
      >
        <FileOutlined /> {record.name}
        {translationStatus ? (
          <Tag color={String(translationStatus).toLowerCase() === 'queued' ? 'gold' : 'blue'} style={{ marginLeft: 8 }}>
            {translationStatus}
          </Tag>
        ) : null}
      </span>
    );
  };

  const renderTypeCell = (record?: DccItemRow) => {
    if (!record) return null;
    return record.isFolder ? 'Folder' : 'File';
  };

  const renderSizeCell = (record?: DccItemRow) => {
    if (!record || record.isFolder || typeof record.sizeBytes !== 'number') return '';
    const bytes = record.sizeBytes;
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(1)} GB`;
  };

  const renderModifiedCell = (record?: DccItemRow) => {
    if (!record || !record.modifiedAt) return '';
    try {
      // Keep it consistent across browsers/locales: YYYY-MM-DD HH:mm
      const d = dayjs(record.modifiedAt * 1000);
      if (!d.isValid()) return '';
      return d.format('YYYY-MM-DD HH:mm');
    } catch {
      return '';
    }
  };

  const renderActionsCell = (record?: DccItemRow) => {
    if (!record) return null;
    if (record.isFolder) {
      const hasChildren = folderHasChildren[record.path] === true;
      return (
        <Space size={6}>
          <Button
            size="small"
            type="primary"
            icon={<FolderOpenOutlined />}
            title="Open"
            onClick={() => handleEnterFolder(record)}
          />
          <Button
            action="delete"
            size="small"
            danger
            icon={<RAiDelete />}
            title={hasChildren ? 'Cannot delete non-empty folder' : 'Delete folder'}
            disabled={hasChildren}
            onClick={() => handleDelete(record)}
          />
        </Space>
      );
    }

    if (canPreviewFile(record.name)) {
      return (
        <Space size={6}>
          <Button
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            title="Preview"
            onClick={() => handlePreview(record)}
          />
          <Button
            action="edit"
            size="small"
            icon={<RDriveFileRename />}
            title="Rename"
            onClick={() => handleRename(record)}
          />
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            title="Open parent folder"
            onClick={() => handleOpenParentFolder(record)}
          />
          <Button
            action="delete"
            size="small"
            danger
            icon={<RAiDelete />}
            title="Delete"
            onClick={() => handleDelete(record)}
          />
        </Space>
      );
    }

    return (
      <Space size={6}>
        <Button
          size="small"
          type="primary"
          icon={<DownloadOutlined />}
          title="Download"
          onClick={() => handleDownload(record)}
        />
        <Button
          action="edit"
          size="small"
          icon={<RDriveFileRename />}
          title="Rename"
          onClick={() => handleRename(record)}
        />
        <Button
          size="small"
          icon={<ArrowUpOutlined />}
          title="Open parent folder"
          onClick={() => handleOpenParentFolder(record)}
        />
        <Button
          action="delete"
          size="small"
          danger
          icon={<RAiDelete />}
          title="Delete"
          onClick={() => handleDelete(record)}
        />
      </Space>
    );
  };

  const breadcrumbItems = [
    {
      title: (
        <span style={{ cursor: 'pointer' }} onClick={() => fetchList('')}>
          <HomeOutlined /> {rootLabel}
        </span>
      ),
    },
    ...currentPath
      .split('/')
      .filter(Boolean)
      .map((segment, idx, arr) => ({
        title: (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => fetchList(arr.slice(0, idx + 1).join('/'))}
          >
            {segment}
          </span>
        ),
      })),
  ];

  const filteredItems: DccItemRow[] = useMemo(() => {
    const source = includeSubfolders ? searchItems : items;
    const nameTerms = String(nameFilter || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const pathTerms = String(pathFilter || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    const start = modifiedRange?.[0] ? dayjs(modifiedRange[0]).startOf('day') : null;
    const end = modifiedRange?.[1] ? dayjs(modifiedRange[1]).endOf('day') : null;

    return source.filter((item) => {
      const name = item.name.toLowerCase();
      const path = String(item.path || '').toLowerCase();
      const isPdf = name.endsWith('.pdf');
      const nameHaystack = name;
      const pathHaystack = includeSubfolders ? `${path}` : '';

      const matchesName = nameTerms.length
        ? nameTerms.every((t) => nameHaystack.includes(t))
        : true;
      const matchesPath = includeSubfolders && pathTerms.length
        ? pathTerms.every((t) => pathHaystack.includes(t))
        : true;

      let matchesType = true;
      switch (typeFilter) {
        case 'folder':
          matchesType = item.isFolder;
          break;
        case 'file':
          matchesType = !item.isFolder;
          break;
        case 'pdf':
          matchesType = !item.isFolder && isPdf;
          break;
        default:
          matchesType = true;
      }

      const matchesModified = (() => {
        if (!start && !end) return true;
        if (!item.modifiedAt) return false;
        const ts = dayjs(item.modifiedAt * 1000);
        if (start && ts.isBefore(start)) return false;
        if (end && ts.isAfter(end)) return false;
        return true;
      })();

      return matchesName && matchesPath && matchesType && matchesModified;
    });
  }, [includeSubfolders, items, modifiedRange, nameFilter, pathFilter, searchItems, typeFilter]);

  useEffect(() => {
    if (!token) return;

    const folderPath = normalizePath(String(currentPath || ''));
    const transmittalNo = inferTransmittalNo(folderPath);

    const shouldLoad = Boolean(folderPath);

    if (!shouldLoad) {
      setTranslationStatusBySource({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSqlData('dcc_translation_log');
        const arr: DccTranslationLogRow[] = Array.isArray(data) ? data : data?.data || [];

        const folderPrefixKey = normalizeSourceKey(folderPath);
        const scoped = (arr || []).filter((r) => {
          const rowFolder = normalizeSourceKey(String(r?.folder_path || ''));
          const rowSource = normalizeSourceKey(String(r?.source_file_path || ''));

          if (transmittalNo) {
            const t = String(r?.transmittal_no || '').toUpperCase();
            if (t === String(transmittalNo).toUpperCase()) return true;
          }

          // Fallback: scope to current folder prefix for responsiveness when transmittal cannot be inferred.
          // This also covers entries where transmittal_no might be inconsistent.
          return Boolean(
            (rowFolder && rowFolder.startsWith(folderPrefixKey)) ||
            (rowSource && rowSource.startsWith(folderPrefixKey))
          );
        });

        const next: Record<string, string> = {};
        scoped.forEach((r) => {
          const srcKey = normalizeSourceKey(String(r?.source_file_path || ''));
          if (!srcKey) return;
          next[srcKey] = String(r?.status || '').trim() || 'queued';
        });

        if (!cancelled) setTranslationStatusBySource(next);
      } catch {
        if (!cancelled) setTranslationStatusBySource({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentPath, effectiveStorageRoot, token]);

  useEffect(() => {
    if (!token) return;
    if (!includeSubfolders) {
      setSearchItems([]);
      setSearchLoading(false);
      return;
    }

    const qName = String(nameFilter || '').trim();
    const qPath = String(pathFilter || '').trim();
    if (allowIndexedSearch && qName.length < 2 && qPath.length < 2) {
      setSearchItems([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      if (cancelled) return;
      setSearchLoading(true);
      (async () => {
        try {
          if (allowIndexedSearch) {
            const res = await dccSearch(
              {
                q: qName || qPath, // keep q populated for backward compatibility
                qName,
                qPath,
                root: currentPath,
                storageRoot: effectiveStorageRoot,
                type: typeFilter as DccSearchType,
                limit: SUBFOLDER_SEARCH_LIMIT,
                useIndex: true,
              },
              token,
              controller.signal
            );

            if (cancelled) return;
            const mapped: DccItemRow[] = (res.items || []).map((it) => {
              const name = String(it.name || '').replace(/\\/g, '/');
              const path = String(it.path || name || '')
                .replace(/\\/g, '/')
                .replace(/^\/+/, '')
                .replace(/\/+$/, '');
              const sizeVal = typeof it.size === 'number' ? it.size : null;

              let modifiedEpoch: number | undefined;
              const rawModified = it.modified_at;
              if (typeof rawModified === 'number') {
                modifiedEpoch = rawModified;
              } else if (typeof rawModified === 'string' && rawModified) {
                const parsed = Date.parse(rawModified);
                if (!Number.isNaN(parsed)) {
                  modifiedEpoch = parsed / 1000;
                }
              }

              return {
                key: path || name,
                name,
                path,
                isFolder: Boolean(it.isFolder),
                sizeBytes: sizeVal ?? undefined,
                modifiedAt: modifiedEpoch,
              };
            });
            setSearchItems(mapped);
            return;
          }

          const foldersToScan = [currentPath];
          const visitedFolders = new Set<string>();
          const nextItems: DccItemRow[] = [];
          const normalizedNameTerms = qName
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
          const normalizedPathTerms = qPath
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);

          while (
            foldersToScan.length &&
            visitedFolders.size < SUBFOLDER_SCAN_LIMIT &&
            nextItems.length < SUBFOLDER_SEARCH_LIMIT
          ) {
            if (cancelled || controller.signal.aborted) {
              return;
            }
            const folderPath = foldersToScan.shift() ?? '';
            if (visitedFolders.has(folderPath)) {
              continue;
            }
            visitedFolders.add(folderPath);

            const stats = await dccListStats(folderPath, token, effectiveStorageRoot, controller.signal);
            const mappedItems = mapDccStatsItems(stats);
            mappedItems.forEach((item: DccItemRow) => {
              if (item.isFolder && !visitedFolders.has(item.path)) {
                foldersToScan.push(item.path);
              }

              const nameHaystack = item.name.toLowerCase();
              const pathHaystack = String(item.path || '').toLowerCase();

              const matchesName = normalizedNameTerms.length
                ? normalizedNameTerms.every((term) => nameHaystack.includes(term))
                : true;
              const matchesPath = normalizedPathTerms.length
                ? normalizedPathTerms.every((term) => pathHaystack.includes(term))
                : true;

              if (!(matchesName && matchesPath) || !matchesTypeFilter(item, typeFilter)) {
                return;
              }

              nextItems.push(item);
            });
          }

          if (!cancelled) {
            setSearchItems(nextItems.slice(0, SUBFOLDER_SEARCH_LIMIT));
          }
        } catch (e) {
          if (cancelled) return;
          if ((e as Error)?.name === 'AbortError') return;
          const msg = String((e as any)?.message || '');
          message.error(msg || 'Failed to search DCC');
          setSearchItems([]);
        } finally {
          if (!cancelled) setSearchLoading(false);
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(handle);
    };
  }, [allowIndexedSearch, currentPath, includeSubfolders, nameFilter, pathFilter, storageRoot, token, typeFilter]);

  const columns = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      width: 300,
      exportValue: (row: DccItemRow) => row?.name ?? '',
      sorter: (a: DccItemRow, b: DccItemRow) => {
        const av = String(a?.name ?? '').toLowerCase();
        const bv = String(b?.name ?? '').toLowerCase();
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      },
      render: (_: any, row: DccItemRow) => renderNameCell(row),
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'isFolder',
      width: 40,
      exportValue: (row: DccItemRow) => renderTypeCell(row) ?? '',
      sorter: (a: DccItemRow, b: DccItemRow) => {
        const av = a?.isFolder ? 0 : 1;
        const bv = b?.isFolder ? 0 : 1;
        return av - bv;
      },
      render: (_: any, row: DccItemRow) => renderTypeCell(row),
    },
    {
      title: 'Size',
      key: 'size',
      dataIndex: 'sizeBytes',
      width: 50,
      exportValue: (row: DccItemRow) => renderSizeCell(row) ?? '',
      sorter: (a: DccItemRow, b: DccItemRow) => {
        const av = typeof a?.sizeBytes === 'number' ? a.sizeBytes : -1;
        const bv = typeof b?.sizeBytes === 'number' ? b.sizeBytes : -1;
        return av - bv;
      },
      render: (_: any, row: DccItemRow) => renderSizeCell(row),
    },
    {
      title: 'Modified',

      key: 'modified',
      dataIndex: 'modifiedAt',
      width: 70,
      exportValue: (row: DccItemRow) => renderModifiedCell(row) ?? '',
      sorter: (a: DccItemRow, b: DccItemRow) => {
        const av = typeof a?.modifiedAt === 'number' ? a.modifiedAt : 0;
        const bv = typeof b?.modifiedAt === 'number' ? b.modifiedAt : 0;
        return av - bv;
      },
      render: (_: any, row: DccItemRow) => renderModifiedCell(row),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      exportValue: () => '',
      render: (_: any, row: DccItemRow) => renderActionsCell(row),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    columnWidth: 15,
    onChange: (keys: React.Key[], rows: DccItemRow[]) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  return (
    <ProtectedRoute>
      <PageGuard>
        <div style={{ padding: 12 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={2}>{title}</Title>

                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: '#389e0d' }}>
                    Green file names indicate the file has already been queued for translation.
                  </Text>
                </div>
              </div>
              <Button size="small" onClick={handleGoUp} disabled={!currentPath}>
                Up One Folder
              </Button>
            </div>

            <Breadcrumb items={breadcrumbItems} />

            <Row gutter={8}>
              <Col xs={24} lg={24}>
                <Card size="small" bodyStyle={{ padding: 12 }}>
                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col xs={24} sm={12}>
                      <Space size={4} wrap={false}>
                        <Input
                          allowClear
                          placeholder="Filter by name"
                          value={nameFilter}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameFilter(e.target.value)}
                          size="small"
                          style={{ width: 240 }}
                        />
                        <RangePicker
                          allowClear
                          size="small"
                          value={(modifiedRange as any) || null}
                          onChange={(v) => setModifiedRange((v as any) || null)}
                          placeholder={['Modified from', 'Modified to'] as any}
                          style={{ width: 240 }}
                        />
                        {includeSubfolders && (
                          <Input
                            allowClear
                            placeholder="Filter by path"
                            value={pathFilter}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPathFilter(e.target.value)}
                            size="small"
                            style={{ width: 200 }}
                          />
                        )}
                      </Space>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space size="small" align="center" wrap={false}>
                          <Space size={6} align="center">
                            <Text type="secondary">Include subfolders</Text>
                            <Switch
                              size="small"
                              checked={includeSubfolders}
                              onChange={(checked) => {
                                setIncludeSubfolders(checked);
                                setSelectedRowKeys([]);
                                setSelectedRows([]);
                                if (!checked) setSearchItems([]);
                              }}
                            />
                          </Space>
                          <Radio.Group
                            size="small"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as 'all' | 'folder' | 'file' | 'pdf')}
                            optionType="button"
                            buttonStyle="solid"
                          >
                            <Radio.Button value="all">All</Radio.Button>
                            <Radio.Button value="folder">Folders</Radio.Button>
                            <Radio.Button value="file">Files</Radio.Button>
                            <Radio.Button value="pdf">PDF only</Radio.Button>
                          </Radio.Group>
                          {allowIndexedSearch && (
                            <Button
                              size="small"
                              type="default"
                              disabled={!currentRoot}
                              loading={reindexLoading}
                              onClick={handleReindexRoot}
                            >
                              Re-index
                            </Button>
                          )}
                          <Text type="secondary">{filteredItems.length} items</Text>
                        </Space>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col xs={24}>
                      <Space size={8} wrap>
                        <Button size="small" onClick={handleBatchDownload} disabled={!selectedFileRows.length}>
                          Download Selected Files
                        </Button>
                        <Button
                          action="add"
                          size="small"
                          type="primary"
                          onClick={handleQueueForTranslation}
                          disabled={!selectedFileRows.length}
                        >
                          Queue for Translation
                        </Button>
                        <Button
                          action="delete"
                          size="small"
                          danger
                          onClick={handleBatchDelete}
                          disabled={!selectedRowKeys.length}
                        >
                          Delete Selected
                        </Button>
                        <Text type="secondary">
                          {selectedRowKeys.length} selected
                          {selectedRowKeys.length > 0
                            ? ` (${selectedFileRows.length} file, ${selectedFolderRows.length} folder)`
                            : ''}
                        </Text>
                      </Space>
                    </Col>
                  </Row>

                  <StandardTable
                    columns={columns}
                    dataSource={filteredItems}
                    rowKey="key"
                    rowSelection={rowSelection}
                    loading={includeSubfolders ? searchLoading : loading}
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </Space>

          <Modal
            title="Queue selected documents for translation"
            open={queueDraftOpen}
            onCancel={() => {
              setQueueDraftOpen(false);
              setQueueDraftRows([]);
            }}
            onOk={saveQueueDraft}
            okText="Queue"
            confirmLoading={queueSaving}
            width={980}
            destroyOnClose
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">
                Folder: <Text code>{String(currentPath || '')}</Text>
              </Text>
              <Text type="secondary">
                Transmittal #: <Text code>{queueDraftRows[0]?.transmittal_no || ''}</Text>
              </Text>
              <StandardTable
                enableExport={false}
                dataSource={queueDraftRows.map((r) => ({ ...r }))}
                rowKey="key"
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                size="small"
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Source file', dataIndex: 'source_file_path', key: 'source_file_path', width: 420 },
                  {
                    title: 'Document #',
                    dataIndex: 'document_no',
                    key: 'document_no',
                    width: 260,
                    render: (_: any, rec: TranslationQueueDraft) => (
                      <Input
                        value={rec.document_no}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQueueDraftRows((prev) => prev.map((p) => (p.key === rec.key ? { ...p, document_no: val } : p)));
                        }}
                        placeholder="Document number"
                        allowClear
                      />
                    ),
                  },
                  {
                    title: 'Rev',
                    dataIndex: 'revision',
                    key: 'revision',
                    width: 120,
                    render: (_: any, rec: TranslationQueueDraft) => (
                      <Input
                        value={rec.revision}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQueueDraftRows((prev) => prev.map((p) => (p.key === rec.key ? { ...p, revision: val } : p)));
                        }}
                        placeholder="Revision"
                        allowClear
                      />
                    ),
                  },
                ]}
              />
            </Space>
          </Modal>

          <FilePreviewModal
            open={isPreviewOpen}
            path={previewPath}
            token={token}
            storageRoot={effectiveStorageRoot}
            onClose={handleClosePreview}
            width="85%"
          />
        </div>
      </PageGuard>
    </ProtectedRoute>
  );
};

export default DccDocuments;


